import { v4 as uuid } from 'uuid';

import unselectedHappyFace from 'assets/images/unselected-happy-face.webp';
import selectedHappyFace from 'assets/images/selected-happy-face.webp';
import unselectedSadFace from 'assets/images/unselected-sad-face.webp';
import selectedSadFace from 'assets/images/selected-sad-face.webp';
import unselectedPainFace from 'assets/images/unselected-pain-face.webp';
import selectedPainFace from 'assets/images/selected-pain-face.webp';

export const HOME_STATUS = {
  IDLE: 'idle',
  PROGRAM: 'program',
  EXERCISES: 'exercises',
  TASK: 'task',
  SQUEEZE: 'squeeze',
  SESSION: 'session',
  FEEDBACK: 'feedback',
};

export const PROFILE_STATUS = {
  IDLE: 'idle',
  PERSONAL: 'personal-data',
  CHANGE_PASSWORD: 'change-password',
  REMINDER: 'reminder',
};

export const SESSION_STATES = {
  DOWNLOAD: 'DOWNLOAD',
  COUNTDOWN: 'COUNTDOWN',
  BREAK: 'BREAK',
  EXERCISE: 'EXERCISE',
};

export const PROGRAM_STATUS = {
  ACTIVE: 'active',
  UNACTIVE: 'unactive',
};

export const LANGUAGES = {
  EN: 'en',
  ES: 'es',
};

export const CHAT_ROLES = {
  USER: 'user',
  ASSISTANT: 'assistant',
};

export const TASK_TYPES = {
  CAPSULE: 'ShortVideo',
  READING: 'Reading',
  AUDIO: 'Audio',
};

export const CHAT_CONTENT_TYPES = {
  CHAT: 'chat',
  EXERCISE: 'exercise',
  AUDIO: 'audio',
  SHORT_VIDEO: 'short_video',
  READING: 'reading',
  SQUEEZE: 'squeeze',
};

export const ACTIVITY_TYPES = {
  EXERCISE: 'ProgramSessionExercise',
  SQUEEZE: 'ProgramSessionSqueeze',
};

export const WELLBEING_OPTIONS = [
  {
    id: uuid(),
    label: 'low',
    value: 1,
    unselectedImage: unselectedHappyFace,
    selectedImage: selectedHappyFace,
  },
  {
    id: uuid(),
    label: 'moderate',
    value: 3,
    unselectedImage: unselectedSadFace,
    selectedImage: selectedSadFace,
  },
  {
    id: uuid(),
    label: 'high',
    value: 5,
    unselectedImage: unselectedPainFace,
    selectedImage: selectedPainFace,
  },
];

export const formatTime = (time) => {
  const minutes = Math.floor(time / 60)
    .toString()
    .padStart(2, '0');
  const seconds = Math.floor(time % 60)
    .toString()
    .padStart(2, '0');
  return `${minutes}:${seconds}`;
};

const highestNumberBelow = (items = [], limit) =>
  items.filter(({ number }) => number < limit).sort((first, second) => second.number - first.number)[0];

// Session and level numbers are not array indices and need not be contiguous, so the session
// completed today is the highest-numbered one below the current, which after a level rollover
// lives at the end of the previous level.
export const resolveLevelAndSession = ({
  programLevels = [],
  levelNumber,
  sessionNumber,
  isSessionCompletedToday = false,
}) => {
  const levelData = programLevels.find(({ number }) => number === levelNumber) || {};
  const { programSessions = [] } = levelData;
  const currentSession = programSessions.find(({ number }) => number === sessionNumber) || {};

  if (!isSessionCompletedToday) return { levelData, sessionData: currentSession };

  const completedSession = highestNumberBelow(programSessions, sessionNumber);
  if (completedSession) return { levelData, sessionData: completedSession };

  const previousLevel = highestNumberBelow(programLevels, levelNumber);
  if (!previousLevel) return { levelData, sessionData: currentSession };

  const { programSessions: previousLevelSessions = [] } = previousLevel;
  return { levelData: previousLevel, sessionData: highestNumberBelow(previousLevelSessions, Infinity) || {} };
};

export const getSqueezeDuration = (squeezes = []) => {
  const duration = squeezes.reduce((accumulator, currSqueeze) => {
    const { squeeze: { content: actions = [] } = {} } = currSqueeze;
    const currSqueezeDuration = actions.reduce((acc, { duration: actionDuration }) => acc + Number(actionDuration), 0);
    return accumulator + currSqueezeDuration;
  }, 0);

  return duration;
};

function getLocalDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function groupMessagesByDay(messages) {
  return messages.reduce((groups, message) => {
    const messageDate = message.createdAt ? new Date(message.createdAt) : null;
    const dateKey = messageDate ? getLocalDateKey(messageDate) : 'unknown';
    const lastGroup = groups[groups.length - 1];

    if (!lastGroup || lastGroup.dateKey !== dateKey) {
      groups.push({ dateKey, messages: [message] });
    } else {
      lastGroup.messages.push(message);
    }
    return groups;
  }, []);
}

function getDayLabel(dateKey, t, locale) {
  if (!dateKey || dateKey === 'unknown') return '';

  const today = new Date();
  const todayKey = getLocalDateKey(today);

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const yesterdayKey = getLocalDateKey(yesterday);

  if (dateKey === todayKey) return t('evieApp.assistant.dayLabel.today');
  if (dateKey === yesterdayKey) return t('evieApp.assistant.dayLabel.yesterday');

  const messageDate = new Date(`${dateKey}T12:00:00`);
  const diffDays = Math.round((today - messageDate) / (1000 * 60 * 60 * 24));

  if (diffDays < 7) return messageDate.toLocaleDateString(locale, { weekday: 'long' });
  return messageDate.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' });
}

export { groupMessagesByDay, getDayLabel };
